import { Component, ChangeDetectionStrategy } from '@angular/core';

import { CommonSuperComponent } from '../common.component.module/common.super';
import { ActivatedRoute, Router } from '@angular/router';
import { RegistrationData } from '../model.module/registration.model';
import { AppConstants } from '../app-constants.service';
import { ValidatorInput } from '../model.module/validation/validation-descriptor.entity';
import { ErrorObservable, MessageObservable } from '../services.module/message.observable';
import { ErrorModel, ErrorSeverity, MessageModel } from '../model.module/modals.model';

import { Menupoint } from '../model.module/menupoint.model';
import { PasswordCriteria } from '../common.component.module/password-criteria';
import { MenuService } from '../providers.module/menu.service';
import { ConfigurationApiService } from '../../generated/api/configuration/configuration.service';
import { RegistrationApiService } from '../../generated/api/registration/registration.service';
import type {
    RegistrationCompleteRequestData,
    RegistrationRequestData
} from '../../generated/api/kreditportalBackendAPI.schemas';

@Component({
    selector: 'app-registration',
    templateUrl: './register-with-pin.component.html',
    styleUrls: ['../common.component.module/common.component.less'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class RegisterWithPINComponent extends CommonSuperComponent {

    error: Response;

    model: RegistrationData = new RegistrationData();

    registrationCompleted = false;
    registrationChecked = false;

    pwCorrectNumChars = false;
    pwCorrectMajuscule = false;
    pwCorrectMinuscule = false;
    pwCorrectDigit = false;
    pwCorrectSpecialChar = false;
    pwCheckNoOtherChars = true;
    pwCorrectPasswordConfirm = false;

    hilfeMenupoint: Menupoint;

    regCheckBtnLocked = false;
    regCompleteBtnLocked = false;

    constructor(
        protected override readonly configurationApiService: ConfigurationApiService,
        private readonly router: Router,
        private readonly activatedRoute: ActivatedRoute,
        private readonly config: AppConstants,
        private readonly menuService: MenuService,
        private readonly messageHandler: MessageObservable,
        private readonly errorObservable: ErrorObservable,
        private readonly registrationApiService: RegistrationApiService
    ) {
        super(config, configurationApiService);

        this.configurationApiService.getConfiguration().subscribe(configuration => {
            this.hilfeMenupoint = this.menuService.getHilfeMenupoint(configuration);
        });

        this.model.loginName.value = this.activatedRoute.snapshot.queryParams['uid'];
    }

    onCheckRegFieldKeyUp(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            this.checkRegistration();
        }
    }

    onCompleteRegFieldKeyUp(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            this.completeRegistration();
        }

        this.updatePwChecklist();
    }

    validateField(field: ValidatorInput): void {
        this.model.validateField(field);
    }

    validatePassword(field: ValidatorInput): void {
        if (this.model.passwordConfirm) {
            this.model.validateField(field);
        }
    }

    validateEmail(field: ValidatorInput): void {
        if (this.model.emailConfirm) {
            this.model.validateField(field);
        }
    }

    gotoLogin(): void {
        this.router.navigate(['/']);
    }

    checkRegistration() {
        this.regCheckBtnLocked = true;

        const validationResult = this.model.validate('loginPIN');
        const valid = validationResult === null;

        if (!valid) {
            this.regCheckBtnLocked = false;
            return;
        }

        console.debug('Registriere', this);

        const registrationRequestData: RegistrationRequestData = {
            loginName: this.model.loginName.value,
            loginPin: this.model.loginPIN.value
        }

        this.registrationApiService.postCheckRegistration(registrationRequestData).subscribe({
            next: registrationResponseData => {
                console.debug('Registrierung erfolgreich geprüft', this);
                this.model.taskId = registrationResponseData.taskId;
                this.registrationChecked = true;
            },
            error: (error: Response) => {
                console.error(error);
                this.registrationChecked = false;
                this.clearInputs(true);
                this.error = error;

                this.errorObservable.notify(new ErrorModel(
                    ErrorSeverity.FATAL,
                    'Registrierungsdaten nicht gefunden',
                    [this.config.messages.registrationCheckNotFound],
                    error
                ));

                this.regCheckBtnLocked = false;
            }
        });
    }

    completeRegistration() {
        this.regCompleteBtnLocked = true;

        const validationResult = this.model.validate();
        const valid = validationResult === null;

        if (!valid) {
            this.regCompleteBtnLocked = false;
            return;
        }

        console.debug('Registrierung vervollständigen', this);

        const request: RegistrationCompleteRequestData = {
            taskId: this.model.taskId,
            vorname: this.model.vorname.value,
            nachname: this.model.nachname.value,
            email: this.model.email.value,
            passwort: {
                recentValue: this.model.password.value,
                changed: true
            },
            url: this.getUrl(`${this.config.loginUrl}?uuid=`)
        }

        this.registrationApiService.postCompleteRegistration(request).subscribe({
            next: res => {
                if (res.completed) {
                    console.debug('Registrierung erfolgreich vervollständigt', this);
                    this.registrationCompleted = true;
                } else {
                    if (!res.passwort.changed) {
                        this.model.password.valid = false;
                        this.model.password.errorMsg = AppConstants.VHV_PASSWORD_NOT_ALLOWED_MSG;
                        this.messageHandler.notify(new MessageModel(
                            'Registrierung konnte nicht abgeschlossen werden.',
                            [AppConstants.VHV_PASSWORD_NOT_ALLOWED_MSG]
                        ));
                    }
                    this.clearInputs(false);
                    this.registrationCompleted = false;
                    this.regCompleteBtnLocked = false;
                }
            },
            error: error => {
                console.error(error);

                this.errorObservable.notify(new ErrorModel(
                    ErrorSeverity.FATAL,
                    'Fehler bei Registrierungdatenübermittlung',
                    [this.config.messages.registrationCheckNotFound],
                    error
                ));

                this.registrationCompleted = false;
                this.clearInputs(false);
                this.error = error;
                this.regCompleteBtnLocked = false;
            }
        })
    }

    private updatePwChecklist(): void {
        const newValue = this.model.password.value;
        const confirmValue = this.model.passwordConfirm;

        const numChars = newValue.length;
        this.pwCorrectNumChars = numChars >= PasswordCriteria.PW_MIN_LENGTH && numChars <= PasswordCriteria.PW_MAX_LENGTH;
        this.pwCorrectMajuscule = PasswordCriteria.PW_CRITERIA_MAJUSCULE.test(newValue);
        this.pwCorrectMinuscule = PasswordCriteria.PW_CRITERIA_MINUSCULE.test(newValue);
        this.pwCorrectDigit = PasswordCriteria.PW_CRITERIA_DIGIT.test(newValue);
        this.pwCorrectSpecialChar = PasswordCriteria.PW_CRITERIA_SPECIAL_CHAR.test(newValue);
        this.pwCheckNoOtherChars = PasswordCriteria.PW_CRITERIA_NO_OTHER_CHARS.test(newValue);
        this.pwCorrectPasswordConfirm = newValue && newValue === confirmValue;

        this.regCompleteBtnLocked = !this.pwCorrectNumChars
            || !this.pwCorrectMajuscule
            || !this.pwCorrectMinuscule
            || !this.pwCorrectDigit
            || !this.pwCorrectSpecialChar
            || !this.pwCheckNoOtherChars
            || !this.pwCorrectPasswordConfirm;
    }


    private clearInputs(all = true) {
        if (all) {
            this.model.loginPIN.value = '';
        }

        this.model.password.value = '';
        this.model.passwordConfirm = '';
    }

}
